import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SingleImageUpload, GalleryUpload } from "@/components/image-upload";
import { PalettePicker } from "@/components/PalettePicker";
import type { PaletteKey } from "@/lib/palettes";
import { LegacyAppNote } from "@/components/treatment-dialog";
import {
  PAYMENT_METHOD_LABELS,
  PAYMENT_METHODS,
  PAYMENT_PROVIDER_LABELS,
  paymentMethodsFor,
  type PaymentMethod,
} from "@/lib/payment-methods";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

const PALETTE_KEYS = ["rose", "sage", "lavender", "sand", "peach", "midnight"] as const;

export const Route = createFileRoute("/_authenticated/settings")({
  component: SettingsPage,
});

// ── Loyalty settings ─────────────────────────────────────────────────────

const loyaltySchema = z.object({
  loyaltyProgramEnabled: z.boolean(),
  loyaltyPointsPerZloty: z.coerce.number().int().min(1).max(100),
  loyaltyPointsRedemptionRate: z.coerce.number().int().min(1).max(1000),
  referralBonusPoints: z.coerce.number().int().min(0).max(10000),
  socialBonusPoints: z.coerce.number().int().min(0).max(10000),
  googleReviewUrl: z.string().url("Niepoprawny adres URL").optional().or(z.literal("")),
  facebookUrl: z.string().url("Niepoprawny adres URL").optional().or(z.literal("")),
  instagramUrl: z.string().url("Niepoprawny adres URL").optional().or(z.literal("")),
});

type LoyaltyFormData = z.infer<typeof loyaltySchema>;

function LoyaltySettingsCard() {
  const { data, isLoading } = trpc.loyalty.getSettings.useQuery();
  const utils = trpc.useUtils();

  const updateSettings = trpc.loyalty.updateSettings.useMutation({
    onSuccess: () => {
      utils.loyalty.getSettings.invalidate();
      toast.success("Zapisano ustawienia programu lojalnościowego");
    },
    onError: (err) => toast.error(err.message),
  });

  const form = useForm<LoyaltyFormData>({
    resolver: zodResolver(loyaltySchema),
    defaultValues: {
      loyaltyProgramEnabled: false,
      loyaltyPointsPerZloty: 1,
      loyaltyPointsRedemptionRate: 10,
      referralBonusPoints: 200,
      socialBonusPoints: 100,
      googleReviewUrl: "",
      facebookUrl: "",
      instagramUrl: "",
    },
  });

  useEffect(() => {
    if (data) {
      form.reset({
        loyaltyProgramEnabled: data.enabled,
        loyaltyPointsPerZloty: data.pointsPerZloty,
        loyaltyPointsRedemptionRate: data.redemptionRate,
        referralBonusPoints: data.referralBonusPoints,
        socialBonusPoints: data.socialBonusPoints,
        googleReviewUrl: data.googleReviewUrl ?? "",
        facebookUrl: data.facebookUrl ?? "",
        instagramUrl: data.instagramUrl ?? "",
      });
    }
  }, [data, form]);

  const onSubmit = form.handleSubmit((values) => {
    updateSettings.mutate({
      ...values,
      googleReviewUrl: values.googleReviewUrl || null,
      facebookUrl: values.facebookUrl || null,
      instagramUrl: values.instagramUrl || null,
    });
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  const enabled = form.watch("loyaltyProgramEnabled");

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">Program lojalnościowy</h2>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="loyaltyEnabled"
            className="h-4 w-4 rounded border-gray-300"
            {...form.register("loyaltyProgramEnabled")}
          />
          <Label htmlFor="loyaltyEnabled">
            Włącz program lojalnościowy
          </Label>
        </div>

        {enabled && (
          <>
            <div className="space-y-2">
              <Label htmlFor="pointsPerZloty">
                Punkty za każdą wydaną złotówkę
              </Label>
              <Input
                id="pointsPerZloty"
                type="number"
                min={1}
                max={100}
                {...form.register("loyaltyPointsPerZloty")}
              />
              <p className="text-xs text-muted-foreground">
                Np. 1 = klient dostaje 1 punkt za każdą wydaną złotówkę
              </p>
              {form.formState.errors.loyaltyPointsPerZloty && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.loyaltyPointsPerZloty.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="redemptionRate">
                Ile punktów = 1 zł zniżki
              </Label>
              <Input
                id="redemptionRate"
                type="number"
                min={1}
                max={1000}
                {...form.register("loyaltyPointsRedemptionRate")}
              />
              <p className="text-xs text-muted-foreground">
                Np. 10 = klient potrzebuje 10 punktów aby uzyskać 1 zł zniżki
              </p>
              {form.formState.errors.loyaltyPointsRedemptionRate && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.loyaltyPointsRedemptionRate.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="referralBonusPoints">
                Punkty za polecenie znajomego
              </Label>
              <Input
                id="referralBonusPoints"
                type="number"
                min={0}
                max={10000}
                {...form.register("referralBonusPoints")}
              />
              <p className="text-xs text-muted-foreground">
                Ile punktów dostaje polecający i nowy klient. 0 = polecenia wyłączone.
              </p>
              {form.formState.errors.referralBonusPoints && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.referralBonusPoints.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="socialBonusPoints">
                Punkty za opinię w Google / odwiedzenie profilu social media
              </Label>
              <Input
                id="socialBonusPoints"
                type="number"
                min={0}
                max={10000}
                {...form.register("socialBonusPoints")}
              />
              <p className="text-xs text-muted-foreground">
                Jednorazowy bonus za każdą z platform poniżej. 0 = bonus wyłączony.
                Klient widzi przycisk w aplikacji tylko dla uzupełnionych linków.
              </p>
              {form.formState.errors.socialBonusPoints && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.socialBonusPoints.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="googleReviewUrl">
                Link do wystawienia opinii w Google
              </Label>
              <Input
                id="googleReviewUrl"
                type="url"
                placeholder="https://search.google.com/local/writereview?placeid=..."
                {...form.register("googleReviewUrl")}
              />
              <p className="text-xs text-muted-foreground">
                Znajdziesz go w Profilu Firmy w Google: "Poproś o opinie".
              </p>
              {form.formState.errors.googleReviewUrl && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.googleReviewUrl.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="facebookUrl">Link do profilu na Facebooku</Label>
              <Input
                id="facebookUrl"
                type="url"
                placeholder="https://www.facebook.com/twojsalon"
                {...form.register("facebookUrl")}
              />
              {form.formState.errors.facebookUrl && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.facebookUrl.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="instagramUrl">Link do profilu na Instagramie</Label>
              <Input
                id="instagramUrl"
                type="url"
                placeholder="https://www.instagram.com/twojsalon"
                {...form.register("instagramUrl")}
              />
              {form.formState.errors.instagramUrl && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.instagramUrl.message}
                </p>
              )}
            </div>
          </>
        )}

        <Button type="submit" disabled={updateSettings.isPending}>
          {updateSettings.isPending ? "Zapisywanie..." : "Zapisz"}
        </Button>
      </form>
    </Card>
  );
}

const tpayScopeSchema = z.object({
  clientId: z.string().min(1, "Client ID jest wymagany"),
  clientSecret: z.string().min(1, "Client Secret jest wymagany"),
});

type TpayScopeFormData = z.infer<typeof tpayScopeSchema>;

function TpayScopeForm({
  title,
  description,
  configured,
  onSave,
  isSaving,
}: {
  title: string;
  description: string;
  configured: boolean;
  onSave: (values: TpayScopeFormData) => void;
  isSaving: boolean;
}) {
  const form = useForm<TpayScopeFormData>({
    resolver: zodResolver(tpayScopeSchema),
    defaultValues: { clientId: "", clientSecret: "" },
  });

  const onSubmit = form.handleSubmit((values) => {
    onSave(values);
    form.reset({ clientId: "", clientSecret: "" });
  });

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded border p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="font-medium">{title}</h3>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        <span
          className={`rounded px-2 py-1 text-xs ${
            configured
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {configured ? "Skonfigurowane" : "Brak"}
        </span>
      </div>
      <div className="space-y-2">
        <Label>Client ID</Label>
        <Input
          placeholder={configured ? "••••••••" : ""}
          {...form.register("clientId")}
        />
        {form.formState.errors.clientId && (
          <p className="text-sm text-destructive">
            {form.formState.errors.clientId.message}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label>Client Secret</Label>
        <Input
          type="password"
          placeholder={configured ? "••••••••" : ""}
          {...form.register("clientSecret")}
        />
        {form.formState.errors.clientSecret && (
          <p className="text-sm text-destructive">
            {form.formState.errors.clientSecret.message}
          </p>
        )}
      </div>
      <Button type="submit" size="sm" disabled={isSaving}>
        {isSaving ? "Zapisywanie..." : configured ? "Zaktualizuj" : "Zapisz"}
      </Button>
    </form>
  );
}


function TpayCredentialsCard() {
  const { data, isLoading } = trpc.salon.getTpayConfigured.useQuery();
  const utils = trpc.useUtils();

  const update = trpc.salon.updateTpayCredentials.useMutation({
    onSuccess: () => {
      utils.salon.getTpayConfigured.invalidate();
      toast.success("Zapisano dane Tpay");
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading || !data) return null;

  return (
    <Card className="p-6">
      <h2 className="mb-1 text-lg font-semibold">Płatności Tpay</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        Klucze API Tpay dla tego salonu. Używane wyłącznie po stronie serwera
        do inicjowania transakcji — płatność klienta kończy się przez Tpay w
        aplikacji mobilnej.
      </p>
      <TpayScopeForm
        title="Klucze API"
        description="Client ID i Client Secret z panelu Tpay. Pozostają wyłącznie na serwerze."
        configured={data.api}
        onSave={(values) => update.mutate({ api: values })}
        isSaving={update.isPending}
      />
    </Card>
  );
}

function PaymentMethodsCard() {
  const { data: salon } = trpc.salon.getOwnerSalon.useQuery();
  const utils = trpc.useUtils();

  const updateSalon = trpc.salon.updateSalon.useMutation({
    onSuccess: () => {
      utils.salon.getOwnerSalon.invalidate();
      toast.success("Zapisano metody płatności");
    },
    onError: (err) => toast.error(err.message),
  });

  if (!salon) return null;

  const enabled = salon.paymentMethods;
  // Only what the salon's provider can take is offered to clients, so only
  // that is worth a checkbox; a method switched on for the other provider
  // stays stored and comes back if the salon is ever switched back.
  const available = paymentMethodsFor(salon.paymentProvider);

  const toggle = (method: PaymentMethod, checked: boolean) => {
    // Built from the full vocabulary, not from `available`: what is switched
    // on for the other provider stays stored and comes back on a switch back.
    const next = PAYMENT_METHODS.filter((m) =>
      m === method ? checked : enabled.includes(m),
    );
    updateSalon.mutate({ paymentMethods: next });
  };

  return (
    <Card className="p-6">
      <h2 className="mb-1 text-lg font-semibold">Metody płatności</h2>
      <p className="mb-4 text-sm text-muted-foreground">
        Metody, którymi klienci mogą zapłacić w aplikacji (operator płatności:{" "}
        {PAYMENT_PROVIDER_LABELS[salon.paymentProvider]}). Dla wybranych
        zabiegów możesz je zawęzić w edycji zabiegu — np. zostawić tylko
        płatność w salonie.
      </p>
      <div className="space-y-3">
        {available.map((method) => {
          const checked = enabled.includes(method);
          const isLast =
            checked && enabled.filter((m) => available.includes(m)).length === 1;
          return (
            <div key={method} className="flex items-center gap-3">
              <input
                type="checkbox"
                id={`pm-${method}`}
                className="h-4 w-4 rounded border-gray-300"
                checked={checked}
                onChange={(e) => toggle(method, e.target.checked)}
                disabled={updateSalon.isPending || isLast}
              />
              <Label htmlFor={`pm-${method}`}>
                {PAYMENT_METHOD_LABELS[method]}
              </Label>
            </div>
          );
        })}
        <p className="text-xs text-muted-foreground">
          Co najmniej jedna metoda musi pozostać włączona.
        </p>
        {(salon.paymentProvider !== "tpay" ||
          !["blik", "paypo", "twisto"].every((m) =>
            enabled.includes(m as PaymentMethod),
          )) && <LegacyAppNote />}
        <p className="text-xs text-muted-foreground">
          <strong>Płatność w salonie:</strong> klient rezerwuje zabieg bez
          płacenia online, a należność pobierasz w recepcji i oznaczasz
          zamówienie jako opłacone przyciskiem „Przyjmij płatność" w zakładce
          Zakupy. Takie zamówienie <strong>nie wygasa</strong> po 24 godzinach —
          w odróżnieniu od nieopłaconych płatności online — więc trafia do
          Ciebie nieopłacone i to Ty ponosisz ryzyko, że klient się nie zjawi.
          Domyślnie wyłączona.
        </p>
      </div>
    </Card>
  );
}

function SubscriptionsCard() {
  const { data: salon } = trpc.salon.getOwnerSalon.useQuery();
  const utils = trpc.useUtils();

  const updateSalon = trpc.salon.updateSalon.useMutation({
    onSuccess: () => {
      utils.salon.getOwnerSalon.invalidate();
      toast.success("Zapisano ustawienia abonamentów");
    },
    onError: (err) => toast.error(err.message),
  });

  if (!salon) return null;

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">Abonamenty</h2>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="subscriptionsEnabled"
            className="h-4 w-4 rounded border-gray-300"
            checked={salon.subscriptionsEnabled}
            onChange={(e) =>
              updateSalon.mutate({ subscriptionsEnabled: e.target.checked })
            }
            disabled={updateSalon.isPending}
          />
          <Label htmlFor="subscriptionsEnabled">
            Sprzedawaj abonamenty miesięczne
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Udostępnia klientom w aplikacji katalog abonamentów: klientka płaci
          stałą kwotę co miesiąc, a w zamian dostaje pulę zabiegów i zniżkę na
          resztę oferty. Opłata pobierana jest automatycznie, więc jest to
          <strong> umowa na czas nieokreślony</strong> — zanim włączysz, ustal
          ceny planów i upewnij się, że masz w grafiku miejsce na wizyty
          abonamentowe. Wyłączenie nie przerywa już wykupionych abonamentów, tylko
          ukrywa ofertę przed nowymi klientami. Domyślnie wyłączone.
        </p>
      </div>
    </Card>
  );
}

function DashboardPrivacyCard() {
  const { data: salon } = trpc.salon.getOwnerSalon.useQuery();
  const utils = trpc.useUtils();

  const updateSalon = trpc.salon.updateSalon.useMutation({
    onSuccess: (updated) => {
      // The saved salon goes straight into the cache, so the dashboard never
      // reads the old setting while the refetch below is still in flight.
      utils.salon.getOwnerSalon.setData(undefined, updated);
      utils.salon.getOwnerSalon.invalidate();
      toast.success("Zapisano ustawienia dashboardu");
    },
    onError: (err) => toast.error(err.message),
  });

  if (!salon) return null;

  return (
    <Card className="p-6">
      <h2 className="mb-4 text-lg font-semibold">Dashboard</h2>
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="hideRevenueStats"
            className="h-4 w-4 rounded border-gray-300"
            checked={salon.hideRevenueStats}
            onChange={(e) =>
              updateSalon.mutate({ hideRevenueStats: e.target.checked })
            }
            disabled={updateSalon.isPending}
          />
          <Label htmlFor="hideRevenueStats">
            Ukryj przychody w statystykach
          </Label>
        </div>
        <p className="text-xs text-muted-foreground">
          Dashboard nie pokaże sumy przychodów z aplikacji ani miesięcznego
          przychodu z abonamentów. Zmienia się wyłącznie widok w panelu — kwoty
          poszczególnych zakupów i rozliczenia działają bez zmian.
        </p>
      </div>
    </Card>
  );
}

const salonSchema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  description: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email("Niepoprawny email").optional().or(z.literal("")),
  legalName: z.string().optional(),
  taxId: z.string().max(32, "Maksymalnie 32 znaki").optional(),
  legalAddress: z.string().optional(),
  palette: z.enum(PALETTE_KEYS).nullable(),
});

type SalonFormData = z.infer<typeof salonSchema>;

function SettingsPage() {
  const { data: salon, isLoading } = trpc.salon.getOwnerSalon.useQuery();
  const { data: gallery } = trpc.salon.getGallery.useQuery(undefined, {
    enabled: !!salon,
  });
  const utils = trpc.useUtils();

  const updateSalon = trpc.salon.updateSalon.useMutation({
    onSuccess: () => {
      utils.salon.getOwnerSalon.invalidate();
      toast.success("Zapisano zmiany");
    },
    onError: (err) => toast.error(err.message),
  });

  const addGalleryImage = trpc.salon.addGalleryImage.useMutation({
    onSuccess: () => utils.salon.getGallery.invalidate(),
  });

  const removeGalleryImage = trpc.salon.removeGalleryImage.useMutation({
    onSuccess: () => utils.salon.getGallery.invalidate(),
  });

  const form = useForm<SalonFormData>({
    resolver: zodResolver(salonSchema),
    defaultValues: {
      name: "",
      description: "",
      address: "",
      phone: "",
      email: "",
      legalName: "",
      taxId: "",
      legalAddress: "",
      palette: null,
    },
  });

  useEffect(() => {
    if (salon) {
      // `getOwnerSalon` is shared with the subscriptions toggle further down
      // this page, so its own mutation re-invalidates this query too — that
      // must not clobber basic-info edits she has not saved yet.
      form.reset(
        {
          name: salon.name,
          description: salon.description ?? "",
          address: salon.address ?? "",
          phone: salon.phone ?? "",
          email: salon.email ?? "",
          legalName: salon.legalName ?? "",
          taxId: salon.taxId ?? "",
          legalAddress: salon.legalAddress ?? "",
          palette: (salon.palette as PaletteKey | null) ?? null,
        },
        { keepDirtyValues: true },
      );
    }
  }, [salon, form]);

  const onSubmit = form.handleSubmit((data) => {
    updateSalon.mutate({
      name: data.name,
      description: data.description || null,
      address: data.address || null,
      phone: data.phone || null,
      email: data.email || null,
      legalName: data.legalName || null,
      taxId: data.taxId || null,
      legalAddress: data.legalAddress || null,
      palette: data.palette,
    });
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  if (!salon) {
    return (
      <p className="text-sm text-muted-foreground">
        Nie jesteś właścicielem żadnego salonu.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Ustawienia salonu</h1>
        <p className="text-sm text-muted-foreground">
          Dane i branding Twojego salonu
        </p>
      </div>

      <form onSubmit={onSubmit}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">
              Informacje podstawowe
            </h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nazwa salonu</Label>
                <Input id="name" {...form.register("name")} />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Opis</Label>
                <Textarea
                  id="description"
                  rows={3}
                  {...form.register("description")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Adres</Label>
                <Input id="address" {...form.register("address")} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input id="phone" {...form.register("phone")} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    {...form.register("email")}
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="text-sm font-semibold">Dane do dokumentów</h3>
                <p className="mb-3 text-xs text-muted-foreground">
                  Uzupełniają regulamin i politykę prywatności na publicznej
                  stronie salonu. Puste pola zastępujemy nazwą i adresem
                  salonu.
                </p>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="legalName">Pełna nazwa firmy</Label>
                    <Input
                      id="legalName"
                      placeholder="np. Salon Piękna Anna Kowalska"
                      {...form.register("legalName")}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="taxId">NIP</Label>
                      <Input
                        id="taxId"
                        placeholder="0000000000"
                        {...form.register("taxId")}
                      />
                      {form.formState.errors.taxId && (
                        <p className="text-sm text-destructive">
                          {form.formState.errors.taxId.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="legalAddress">Adres rejestrowy</Label>
                      <Input
                        id="legalAddress"
                        placeholder="jeśli inny niż adres salonu"
                        {...form.register("legalAddress")}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">Branding</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Kod salonu</Label>
                <Input disabled value={salon.code} />
                <p className="text-xs text-muted-foreground">
                  Kod używany przez klientów do dołączenia w aplikacji
                </p>
              </div>
              <div className="space-y-2">
                <div>
                  <Label>Paleta aplikacji mobilnej</Label>
                  <p className="text-xs text-muted-foreground">
                    Klienci zobaczą aplikację w wybranych kolorach. Każda
                    paleta definiuje pełen motyw — tła, kolory akcentów,
                    typografię.
                  </p>
                </div>
                <PalettePicker
                  value={form.watch("palette")}
                  onChange={(key) => form.setValue("palette", key, { shouldDirty: true })}
                />
              </div>
              <div className="space-y-2">
                <Label>Logo salonu</Label>
                <SingleImageUpload
                  value={salon.logoUrl}
                  onChange={(url) =>
                    updateSalon.mutate({ logoUrl: url })
                  }
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="mt-6">
          <Button type="submit" disabled={updateSalon.isPending}>
            {updateSalon.isPending ? "Zapisywanie..." : "Zapisz zmiany"}
          </Button>
        </div>
      </form>

      <PaymentMethodsCard />

      <SubscriptionsCard />

      <DashboardPrivacyCard />

      <LoyaltySettingsCard />

      {/* Keys only matter to the provider that takes the salon's payments. */}
      {salon.paymentProvider === "tpay" && <TpayCredentialsCard />}

      <Card className="p-6">
        <h2 className="mb-4 text-lg font-semibold">Galeria salonu</h2>
        <p className="mb-3 text-sm text-muted-foreground">
          Zdjęcia widoczne dla klientów w aplikacji mobilnej
        </p>
        <GalleryUpload
          images={
            gallery?.map((img) => ({ id: img.id, url: img.url })) ?? []
          }
          onAdd={(url) => addGalleryImage.mutate({ url })}
          onRemove={(id) => removeGalleryImage.mutate({ imageId: id })}
        />
      </Card>
    </div>
  );
}
